import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';
import { amountInWordsINR } from './amountInWords';

const SectionDivider = ({ icon, title }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.4rem 0.8rem',
            borderRadius: '6px',
            background: 'linear-gradient(90deg, #f1f5f9, #f8fafc)',
            borderLeft: '3px solid #6d3fbf',
            margin: '1rem 0 0.75rem'
        }}
    >
        <i className={icon} style={{ color: '#6d3fbf', fontSize: '0.8rem' }} />
        <span style={{ fontWeight: '600', fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
    </div>
);

const FieldLabel = ({ children, required }) => (
    <label style={{ fontWeight: '600', fontSize: '0.8rem', color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
        {children} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
);

const emptyItem = () => ({ description: '', sac_code: '', qty: 1, rate: 0, amount: 0 });

const STATUS_OPTIONS = ['Draft', 'Sent', 'Paid', 'Overdue', 'Cancelled'].map((s) => ({ label: s, value: s }));

const TaxInvoiceModal = ({ visible, onHide, invoice, onSave, buyers, sellers, defaultSeller, nextInvoiceNo }) => {
    const [form, setForm] = useState(null);
    const [interState, setInterState] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const isEdit = !!form?.invoice_id;

    useEffect(() => {
        if (!visible) return;
        if (invoice?.invoice_id) {
            setForm({
                ...invoice,
                invoice_date: invoice.invoice_date ? new Date(invoice.invoice_date) : new Date(),
                due_date: invoice.due_date ? new Date(invoice.due_date) : null,
                items: invoice.items?.length ? invoice.items.map((i) => ({ ...i })) : [emptyItem()]
            });
            setInterState(Number(invoice.igst_amount) > 0);
        } else {
            setForm({
                invoice_no: nextInvoiceNo || '',
                invoice_date: new Date(),
                due_date: null,
                buyer_id: null,
                buyer_name: '',
                buyer_address: '',
                buyer_gst_no: '',
                seller_id: defaultSeller?.setting_id || null,
                seller_name: defaultSeller?.company_name || '',
                seller_address: defaultSeller?.address || '',
                seller_gst_no: defaultSeller?.gst_no || '',
                seller_phone: defaultSeller?.phone || '',
                seller_email: defaultSeller?.email || '',
                seller_website: defaultSeller?.website || '',
                seller_bank_name: defaultSeller?.bank_name || '',
                seller_account_no: defaultSeller?.account_no || '',
                seller_ifsc_code: defaultSeller?.ifsc_code || '',
                seller_bank_branch: defaultSeller?.bank_branch || '',
                mode_of_payment: 'Other',
                reference_no: '',
                terms_of_delivery: '',
                status: 'Draft',
                remarks: '',
                items: [emptyItem()]
            });
            setInterState(false);
        }
        setSubmitted(false);
    }, [visible, invoice, nextInvoiceNo, defaultSeller]);

    const totals = useMemo(() => {
        const items = form?.items || [];
        const subtotal = items.reduce((sum, i) => sum + Number(i.qty || 0) * Number(i.rate || 0), 0);
        const cgstPercent = interState ? 0 : 9;
        const sgstPercent = interState ? 0 : 9;
        const igstPercent = interState ? 18 : 0;
        const cgstAmount = (subtotal * cgstPercent) / 100;
        const sgstAmount = (subtotal * sgstPercent) / 100;
        const igstAmount = (subtotal * igstPercent) / 100;
        const grandTotal = subtotal + cgstAmount + sgstAmount + igstAmount;
        return { subtotal, cgstPercent, sgstPercent, igstPercent, cgstAmount, sgstAmount, igstAmount, grandTotal };
    }, [form?.items, interState]);

    if (!form) return null;

    const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const handleBuyerChange = (buyerId) => {
        const buyer = (buyers || []).find((b) => String(b.buyer_id) === String(buyerId));
        setForm((prev) => ({
            ...prev,
            buyer_id: buyerId,
            buyer_name: buyer?.buyer_name || '',
            buyer_address: buyer?.address || '',
            buyer_gst_no: buyer?.gst_no || ''
        }));
    };

    const handleSellerChange = (sellerId) => {
        const seller = (sellers || []).find((s) => String(s.setting_id) === String(sellerId));
        setForm((prev) => ({
            ...prev,
            seller_id: sellerId,
            seller_name: seller?.company_name || '',
            seller_address: seller?.address || '',
            seller_gst_no: seller?.gst_no || '',
            seller_phone: seller?.phone || '',
            seller_email: seller?.email || '',
            seller_website: seller?.website || '',
            seller_bank_name: seller?.bank_name || '',
            seller_account_no: seller?.account_no || '',
            seller_ifsc_code: seller?.ifsc_code || '',
            seller_bank_branch: seller?.bank_branch || ''
        }));
    };

    const updateItem = (idx, field, value) => {
        setForm((prev) => {
            const items = prev.items.map((item, i) => {
                if (i !== idx) return item;
                const next = { ...item, [field]: value };
                if (field === 'qty' || field === 'rate') {
                    next.amount = Number(next.qty || 0) * Number(next.rate || 0);
                }
                return next;
            });
            return { ...prev, items };
        });
    };

    const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
    const removeItem = (idx) => setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

    const isInvalid = (name) => {
        if (!submitted) return false;
        if (name === 'invoice_no') return !form.invoice_no?.trim();
        if (name === 'invoice_date') return !form.invoice_date;
        if (name === 'buyer_name') return !form.buyer_name?.trim();
        if (name === 'items') return !form.items?.some((i) => i.description?.trim() && Number(i.amount) > 0);
        return false;
    };

    const handleSave = () => {
        setSubmitted(true);
        const validItems = (form.items || []).filter((i) => i.description?.trim() && Number(i.amount) > 0);
        const valid = form.invoice_no?.trim() && form.invoice_date && form.buyer_name?.trim() && validItems.length > 0;
        if (!valid) return;

        const payload = {
            ...form,
            invoice_date: form.invoice_date.toISOString().split('T')[0],
            due_date: form.due_date ? form.due_date.toISOString().split('T')[0] : null,
            items: validItems,
            subtotal: totals.subtotal,
            cgst_percent: totals.cgstPercent,
            cgst_amount: totals.cgstAmount,
            sgst_percent: totals.sgstPercent,
            sgst_amount: totals.sgstAmount,
            igst_percent: totals.igstPercent,
            igst_amount: totals.igstAmount,
            grand_total: totals.grandTotal,
            amount_in_words: amountInWordsINR(totals.grandTotal),
            updated_by: localStorage.getItem('username') || localStorage.getItem('userId') || 'System'
        };
        onSave(payload);
    };

    const dialogHeader = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span
                style={{
                    width: '2.25rem',
                    height: '2.25rem',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #6d3fbf, #8b5cf6)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <i className="pi pi-file" style={{ color: '#fff', fontSize: '0.95rem' }} />
            </span>
            <div style={{ lineHeight: 1.3 }}>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{isEdit ? 'Edit Tax Invoice' : 'New Tax Invoice'}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{isEdit ? 'Update invoice details' : 'AMC, domain renewal, SSL and other client billing'}</div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button label="Cancel" icon="pi pi-times" outlined severity="secondary" onClick={onHide} style={{ minWidth: '100px' }} />
            <Button label={isEdit ? 'Update' : 'Save'} icon="pi pi-check" severity="success" onClick={handleSave} style={{ minWidth: '100px' }} />
        </div>
    );

    return (
        <Dialog visible={visible} style={{ width: '920px' }} breakpoints={{ '1024px': '95vw' }} header={dialogHeader} modal className="p-fluid" footer={dialogFooter} onHide={onHide} contentStyle={{ padding: '1rem 1.25rem 0.75rem' }}>
            <SectionDivider icon="pi pi-file-edit" title="Invoice Details" />
            <div className="grid" style={{ margin: 0 }}>
                <div className="field col-12 md:col-3" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Invoice No</FieldLabel>
                    <InputText value={form.invoice_no || ''} onChange={(e) => setField('invoice_no', e.target.value)} className={classNames('w-full', { 'p-invalid': isInvalid('invoice_no') })} />
                </div>
                <div className="field col-12 md:col-3" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Invoice Date</FieldLabel>
                    <Calendar value={form.invoice_date} onChange={(e) => setField('invoice_date', e.value)} dateFormat="dd-M-yy" showIcon className={classNames({ 'p-invalid': isInvalid('invoice_date') })} />
                </div>
                <div className="field col-12 md:col-3" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Due Date</FieldLabel>
                    <Calendar value={form.due_date} onChange={(e) => setField('due_date', e.value)} dateFormat="dd-M-yy" showIcon />
                </div>
                <div className="field col-12 md:col-3" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Status</FieldLabel>
                    <Dropdown value={form.status} options={STATUS_OPTIONS} onChange={(e) => setField('status', e.value)} />
                </div>

                <div className="field col-12 md:col-4" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Mode/Terms of Payment</FieldLabel>
                    <InputText value={form.mode_of_payment || ''} onChange={(e) => setField('mode_of_payment', e.target.value)} className="w-full" />
                </div>
                <div className="field col-12 md:col-4" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Reference</FieldLabel>
                    <InputText value={form.reference_no || ''} onChange={(e) => setField('reference_no', e.target.value)} className="w-full" />
                </div>
                <div className="field col-12 md:col-4" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Terms Of Delivery</FieldLabel>
                    <InputText value={form.terms_of_delivery || ''} onChange={(e) => setField('terms_of_delivery', e.target.value)} className="w-full" />
                </div>
            </div>

            <SectionDivider icon="pi pi-briefcase" title="Seller" />
            <div className="grid" style={{ margin: 0 }}>
                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Seller (from Seller Master)</FieldLabel>
                    <Dropdown
                        value={form.seller_id}
                        options={sellers}
                        onChange={(e) => handleSellerChange(e.value)}
                        optionLabel="company_name"
                        optionValue="setting_id"
                        placeholder="Select seller"
                        filter
                        className="w-full"
                    />
                </div>
                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Seller GST No</FieldLabel>
                    <InputText value={form.seller_gst_no || ''} disabled className="w-full" />
                </div>
            </div>

            <SectionDivider icon="pi pi-user" title="Buyer" />
            <div className="grid" style={{ margin: 0 }}>
                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Buyer (from Buyer Master — autofills details)</FieldLabel>
                    <Dropdown
                        value={form.buyer_id}
                        options={buyers}
                        onChange={(e) => handleBuyerChange(e.value)}
                        optionLabel="buyer_name"
                        optionValue="buyer_id"
                        placeholder="Select buyer"
                        filter
                        showClear
                        className="w-full"
                    />
                </div>
                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel required>Buyer Name</FieldLabel>
                    <InputText value={form.buyer_name || ''} onChange={(e) => setField('buyer_name', e.target.value)} className={classNames('w-full', { 'p-invalid': isInvalid('buyer_name') })} />
                </div>
                <div className="field col-12 md:col-6" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Buyer GST No</FieldLabel>
                    <InputText value={form.buyer_gst_no || ''} onChange={(e) => setField('buyer_gst_no', e.target.value)} className="w-full" />
                </div>
                <div className="field col-12" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Buyer Address</FieldLabel>
                    <InputTextarea value={form.buyer_address || ''} onChange={(e) => setField('buyer_address', e.target.value)} rows={2} className="w-full" />
                </div>
            </div>

            <SectionDivider icon="pi pi-list" title="Line Items" />
            {isInvalid('items') && <small className="p-error" style={{ display: 'block', marginBottom: '0.5rem' }}>At least one item with description and amount is required.</small>}
            <div style={{ overflowX: 'auto' }}>
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.75rem', color: '#64748b' }}>Description</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.75rem', color: '#64748b', width: '110px' }}>SAC Code</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.75rem', color: '#64748b', width: '90px' }}>Qty</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.75rem', color: '#64748b', width: '130px' }}>Rate</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', color: '#64748b', width: '130px' }}>Amount</th>
                            <th style={{ width: '40px' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {form.items.map((item, idx) => (
                            <tr key={idx}>
                                <td style={{ padding: '4px' }}>
                                    <InputText value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Software Maintenance, Domain Renewal, SSL..." className="w-full" />
                                </td>
                                <td style={{ padding: '4px' }}>
                                    <InputText value={item.sac_code} onChange={(e) => updateItem(idx, 'sac_code', e.target.value)} placeholder="998314" className="w-full" />
                                </td>
                                <td style={{ padding: '4px' }}>
                                    <InputNumber value={item.qty} onValueChange={(e) => updateItem(idx, 'qty', e.value ?? 0)} min={0} className="w-full" />
                                </td>
                                <td style={{ padding: '4px' }}>
                                    <InputNumber value={item.rate} onValueChange={(e) => updateItem(idx, 'rate', e.value ?? 0)} mode="decimal" minFractionDigits={2} min={0} className="w-full" />
                                </td>
                                <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>{Number(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                    <Button icon="pi pi-trash" text severity="danger" onClick={() => removeItem(idx)} disabled={form.items.length === 1} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Button label="Add Line Item" icon="pi pi-plus" text onClick={addItem} style={{ marginTop: '0.5rem' }} />

            <SectionDivider icon="pi pi-percentage" title="Tax & Totals" />
            <div className="grid" style={{ margin: 0, alignItems: 'flex-end' }}>
                <div className="field col-12 md:col-5" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Tax Type</FieldLabel>
                    <Dropdown
                        value={interState}
                        options={[
                            { label: 'Intra-state — CGST 9% + SGST 9%', value: false },
                            { label: 'Inter-state — IGST 18%', value: true }
                        ]}
                        onChange={(e) => setInterState(e.value)}
                        className="w-full"
                    />
                </div>
                <div className="field col-12 md:col-7">
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ color: '#64748b' }}>Sub Total</span>
                            <span>{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {interState ? (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ color: '#64748b' }}>IGST (18%)</span>
                                <span>{totals.igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ color: '#64748b' }}>CGST (9%)</span>
                                    <span>{totals.cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ color: '#64748b' }}>SGST (9%)</span>
                                    <span>{totals.sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                </div>
                            </>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, marginTop: 4, borderTop: '2px solid #6d3fbf', fontWeight: 700, color: '#6d3fbf' }}>
                            <span>Grand Total</span>
                            <span>{totals.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>{amountInWordsINR(totals.grandTotal)}</div>
                    </div>
                </div>

                <div className="field col-12" style={{ paddingBottom: '0.5rem' }}>
                    <FieldLabel>Remarks</FieldLabel>
                    <InputTextarea value={form.remarks || ''} onChange={(e) => setField('remarks', e.target.value)} rows={2} className="w-full" placeholder="Payment is to be made on presentation of Invoice or before the Due date mentioned on the Invoice." />
                </div>
            </div>
        </Dialog>
    );
};

export default TaxInvoiceModal;
